
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Copy, Gift, RefreshCw } from "lucide-react";
import { useHandoverToken } from "@/hooks/useHandoverToken";
import { useToast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";

interface HandoverTokenInterfaceProps {
  className?: string;
}

export const HandoverTokenInterface = ({ className }: HandoverTokenInterfaceProps) => {
  const { claimProfileWithToken, copyTokenToClipboard, isClaiming } = useHandoverToken();
  const { toast } = useToast();
  const [tokenInput, setTokenInput] = useState("");

  const handleClaimProfile = async () => {
    if (!tokenInput.trim()) {
      toast({
        title: "Token required",
        description: "Please enter a handover token to claim a profile.",
        variant: "destructive"
      });
      return;
    }

    try {
      await claimProfileWithToken(tokenInput);
      setTokenInput("");
      // Refresh the page to show the new profile
      window.location.reload();
    } catch (error) {
      // Error already handled by the hook
    }
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gift className="h-5 w-5 text-uplivion-blue" />
          Timeline Handover
        </CardTitle>
        <CardDescription>
          Claim a timeline that has been shared with you using a handover token
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="token-input">Handover Token</Label>
          <div className="flex gap-2">
            <Input
              id="token-input"
              placeholder="Enter 6-character token (e.g., ABC123)"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value.toUpperCase())}
              maxLength={6}
              className="font-mono"
            />
            <Button
              onClick={handleClaimProfile}
              disabled={isClaiming || !tokenInput.trim()}
              className="gap-2"
            >
              {isClaiming ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Gift className="h-4 w-4" />
              )}
              {isClaiming ? 'Claiming...' : 'Claim'}
            </Button>
          </div>
        </div>

        <div className="text-sm text-muted-foreground space-y-2">
          <p className="font-medium">How it works:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Someone creates a timeline and generates a handover token</li>
            <li>They share the 6-character token with you</li>
            <li>Enter the token above to claim ownership of their timeline</li>
            <li>Once claimed, you can manage their memories and timeline</li>
          </ul>
        </div>

        <Separator />

        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-start gap-2">
            <Badge variant="outline" className="mt-0.5">
              Tip
            </Badge>
            <div className="text-sm">
              <p className="font-medium text-blue-900 mb-1">
                Creating timelines for others?
              </p>
              <p className="text-blue-700">
                You can create gift timelines or memorial timelines and generate handover tokens 
                to transfer ownership when ready. Perfect for surprise gifts or family memories!
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
